/** @format */

import PropTypes from "prop-types";

import { useEffect, useState } from "react";

// material-ui
import {
	Avatar,
	Box,
	CardContent,
	IconButton,
	PaletteColorOptions,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

// project imports
import MainCard from "../cards/MainCard";
import SkeletonCurrentContributorsCard from "../cards/skeleton/SkeletonCurrentContributorsCard";
import { AppDispatch } from "../../redux/store/store";
import { useParams } from "react-router-dom";

// assets
import { useDispatch, useSelector } from "react-redux";
import { selectCircleData } from "../../redux/selectors/circleSelector";
import {
	selectCurrentCycleById,
	selectCurrentCycleOfCircle,
} from "../../redux/selectors/contributionSelector";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { getCurrentCycleById } from "../../redux/actions/contributionAction";
import Label from "../label/Label";
import { formatCurrency } from "../../utility/currencyUtils";

declare module "@mui/material/styles" {
	interface PaletteColor {
		800?: string;
	}
	interface SimplePaletteColorOptions {
		800?: string;
	}

	interface TypographyVariants {
		commonAvatar: React.CSSProperties;
		smallAvatar: React.CSSProperties;
		mediumAvatar: React.CSSProperties;
		largeAvatar: React.CSSProperties;
	}

	// Allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		commonAvatar?: React.CSSProperties;
		smallAvatar?: React.CSSProperties;
		mediumAvatar: React.CSSProperties;

		largeAvatar?: React.CSSProperties;
	}

	interface Palette {
		dark: PaletteColor;
	}
	interface PaletteOptions {
		dark?: PaletteColorOptions;
	}

	interface Palette {
		orange: PaletteColor;
	}
	interface PaletteOptions {
		orange?: PaletteColorOptions;
	}
}

export interface CurrentContributorsCardProps {
	isLoading: boolean;
	// firstName?: string;
	// lastName?: string;
	// contributionAmount?: number;
	// profileImage?: string;
}

const CurrentContributorsCard = (props: CurrentContributorsCardProps) => {
	const { isLoading } = props;

	const dispatch = useDispatch<AppDispatch>();

	const user = useSelector(selectCurrentUser);
	const { circleId } = useParams();

	const circleData = useSelector(selectCircleData);
	const currency = (circleData as any)?.configuration?.currency || "usd";
	const currentCycle = useSelector((state: any) =>
		selectCurrentCycleOfCircle(state, circleData?._id ?? ""),
	);

	const cycleByIdResult = useSelector((state: any) =>
		selectCurrentCycleById(state, circleData?._id ?? ""),
	);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(4);
	const [isRefreshing, setIsRefreshing] = useState(false);

	useEffect(() => {
		if (user?._id && circleId && circleData?._id && currentCycle?._id) {
			dispatch(
				getCurrentCycleById({
					unionId: circleData._id,
					cycleId: currentCycle._id,
				}),
			);
		}
	}, [dispatch, user?._id, circleId, circleData?._id, currentCycle?._id]);

	const handleRefresh = async () => {
		if (circleData?._id && currentCycle?._id) {
			setIsRefreshing(true);
			try {
				await dispatch(
					getCurrentCycleById({
						unionId: circleData._id,
						cycleId: currentCycle._id,
					}),
				).unwrap();
			} finally {
				setIsRefreshing(false);
			}
		}
	};

	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 8));
		setPage(0);
	};

	const sortedContributors =
		cycleByIdResult?.contributions ?
			[...cycleByIdResult.contributions].sort((a: any, b: any) => {
				const getTimestamp = (item: any) => {
					return (
						item.createdAtUTC ||
						item.created_At ||
						item.createdAt ||
						item.updatedAt ||
						item.updated_At ||
						new Date().toISOString()
					);
				};

				const timestampA = new Date(getTimestamp(a)).getTime();
				const timestampB = new Date(getTimestamp(b)).getTime();

				return timestampB - timestampA;
			})
			: [];

	const paginatedContributors = sortedContributors.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const formatDate = (dateString: string) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<>
			{isLoading ?
				<SkeletonCurrentContributorsCard />
				: <MainCard content={false}>
					<CardContent sx={{ pb: 0 }}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ mb: 2 }}>
							<Stack direction="row" alignItems="center">
								<Typography variant="h4">Current Contributors</Typography>
								<Tooltip title="Refresh contributors">
									<IconButton
										onClick={handleRefresh}
										disabled={isRefreshing}
										size="small"
										sx={{ ml: 1 }}>
										<RefreshIcon
											fontSize="small"
											sx={{
												animation:
													isRefreshing ? "spin 1s linear infinite" : "none",
												"@keyframes spin": {
													"0%": { transform: "rotate(0deg)" },
													"100%": { transform: "rotate(360deg)" },
												},
											}}
										/>
									</IconButton>
								</Tooltip>
							</Stack>
							<Typography variant="body2" color="text.secondary">
								{sortedContributors.length} contribution
								{sortedContributors.length !== 1 ? "s" : ""}
							</Typography>
						</Stack>

						{currentCycle && currentCycle.status === "active" ?
							<>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 600 }}>Member</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="center">
													Amount
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="center">
													Status
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }} align="right">
													Date
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{paginatedContributors.length > 0 ?
												paginatedContributors.map((contribution: any) => (
													<TableRow
														key={contribution._id}
														hover
														sx={{
															"&:last-child td, &:last-child th": { border: 0 },
														}}>
														<TableCell>
															<Stack
																direction="row"
																alignItems="center"
																spacing={1.5}>
																<Avatar
																	alt={`${contribution.memberId?.firstName ?? ""} ${contribution.memberId?.lastName ?? ""}`}
																	src={contribution.memberId?.profileImage}
																	sx={{ width: 32, height: 32 }}
																/>
																<Typography variant="body2">
																	{contribution.memberId?.firstName ??
																		"Unknown"}{" "}
																	{contribution.memberId?.lastName ?? ""}
																</Typography>
															</Stack>
														</TableCell>
														<TableCell align="center">
															<Typography
																variant="body2"
																sx={{ fontWeight: 500 }}>
																{formatCurrency(
																	Number(
																		contribution.contributionAmount,
																	), currency)}
															</Typography>
														</TableCell>
														<TableCell align="center">
															<Label color="success">Completed</Label>
														</TableCell>
														<TableCell align="right">
															<Typography
																variant="body2"
																color="text.secondary">
																{formatDate(
																	contribution.created_At ||
																	contribution.createdAt,
																)}
															</Typography>
														</TableCell>
													</TableRow>
												))
												: <TableRow>
													<TableCell colSpan={4} align="center">
														<Typography
															variant="body2"
															color="text.secondary"
															sx={{ py: 2 }}>
															No contributions yet
														</Typography>
													</TableCell>
												</TableRow>
											}
										</TableBody>
									</Table>
								</TableContainer>
								{sortedContributors.length > rowsPerPage && (
									<TablePagination
										component="div"
										count={sortedContributors.length}
										page={page}
										onPageChange={handleChangePage}
										rowsPerPage={rowsPerPage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										rowsPerPageOptions={[]}
										labelDisplayedRows={({ page, count }) =>
											`Page ${page + 1} of ${Math.ceil(count / rowsPerPage)}`
										}
										sx={{
											".MuiTablePagination-selectLabel": { display: "none" },
											".MuiTablePagination-select": { display: "none" },
											".MuiTablePagination-selectIcon": { display: "none" },
											".MuiTablePagination-spacer": { display: "none" },
											".MuiTablePagination-toolbar": {
												justifyContent: "center",
												minHeight: "40px",
											},
										}}
									/>
								)}
							</>
							: <Box sx={{ py: 3, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									No active contribution cycle
								</Typography>
							</Box>
						}
					</CardContent>
				</MainCard>
			}
		</>
	);
};

CurrentContributorsCard.propTypes = {
	isLoading: PropTypes.bool,
};

export default CurrentContributorsCard;
